import { useRef } from 'react'
import cx from 'classnames'
import Styles from './Message.module.scss'
import { LastMessageActionsProps } from './Types'
import { useButton } from '@react-aria/button'
import { Text } from 'blaze-ui/Text/Text'
import { useRetryMessage } from 'hooks/queries/chat/useRetryMessage'
import { Button } from 'blaze-ui/Button'
import { ThumbDown, ThumbUp } from 'blaze-ui/icons'
import { useRateMessage } from 'hooks/queries/chat/useRateMessage'
import { ChatDesignPreviewContent, ChatRatingContent } from 'api/clients/ChatClient'
import { generatePath, useHistory } from 'react-router-dom'
import { DESIGN_PATH } from 'paths'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'
import { useIsSystemRedesign } from 'hooks/useIsSystemRedesign'

interface RatingButtonProps {
  onPress: () => void
  icon: React.ReactNode
  isDisabled: boolean
  'aria-label'?: string
}

const RatingButton = ({ onPress, icon, isDisabled, ...rest }: RatingButtonProps) => {
  const ref = useRef<HTMLDivElement>(null)
  const { buttonProps } = useButton({ onPress, elementType: 'div', isDisabled }, ref)
  return (
    <div {...buttonProps} {...rest} className={cx(Styles.ratingButton, { [Styles.disabled]: isDisabled })}>
      {icon}
    </div>
  )
}

export const LastMessageActions = observer(({ message }: LastMessageActionsProps) => {
  const isSystemRedesignEnabled = useIsSystemRedesign()
  const { designStore } = useRootStore()
  const { mutate: retryMessage, isLoading: isRetryLoading } = useRetryMessage()
  const { mutate: rateMessage, isLoading: isRateLoading } = useRateMessage()
  const history = useHistory()
  const ratingMessage = message.content.find((content): content is ChatRatingContent => content.type === 'rated')
  const rating = ratingMessage?.rating
  const designContentMessage = message.content.find(
    (content): content is ChatDesignPreviewContent => content.type === 'design_content',
  )
  const { design_id } = designContentMessage || {}
  const isInDesigner = design_id && designStore?.fileId === design_id

  const handleRetry = () => {
    retryMessage({ threadId: message.thread_id, id: message.id })
  }

  const handleRate = (rate: 'upvote' | 'downvote') => {
    rateMessage({ threadId: message.thread_id, messageId: message.id, rating: rate })
  }

  const handleOpenDesign = () => {
    if (design_id) {
      history.push(generatePath(DESIGN_PATH, { key: design_id }))
    }
  }

  return (
    <div className={Styles.actions}>
      <div className={Styles.rating}>
        <Text variant="secondary" color="var(--dark-60)">
          Do you like these results?
        </Text>
        <div className={Styles.ratingButtons}>
          <RatingButton
            aria-label="Upvote"
            onPress={() => handleRate('upvote')}
            icon={<ThumbUp size={16} color={rating === 'upvote' ? 'var(--dark-100)' : 'var(--dark-60)'} />}
            isDisabled={isRateLoading}
          />
          <RatingButton
            aria-label="Downvote"
            onPress={() => handleRate('downvote')}
            icon={<ThumbDown size={16} color={rating === 'downvote' ? 'var(--dark-100)' : 'var(--dark-60)'} />}
            isDisabled={isRateLoading}
          />
        </div>
      </div>
      <div className={Styles.actionsButtons}>
        <Button
          rounded={!isSystemRedesignEnabled}
          variant={isSystemRedesignEnabled ? 'secondary' : 'tertiary'}
          size={isSystemRedesignEnabled ? 'md' : 'sm'}
          onPress={handleRetry}
          isDisabled={isRetryLoading}
          className={Styles.actionButton}
        >
          <Text variant="secondary" color="var(--dark-100)">
            Try Again
          </Text>
        </Button>
        {design_id && !isInDesigner && (
          <Button
            rounded={!isSystemRedesignEnabled}
            variant={isSystemRedesignEnabled ? 'secondary' : 'tertiary'}
            size={isSystemRedesignEnabled ? 'md' : 'sm'}
            onPress={handleOpenDesign}
            className={Styles.actionButton}
          >
            <Text variant="secondary" color="var(--dark-100)">
              Edit in Designer
            </Text>
          </Button>
        )}
      </div>
    </div>
  )
})
